import type { TooltipWalkthroughStep } from '@unifyapps/defs/types/page/walkthrough';
import { useContainerElementContext } from '@unifyapps/ui/providers/ContainerElementContext';
import { useEffect } from 'react';
import { ARENA_SELECTOR } from '../../const';
import {
  useGetWalkthroughManagerStore,
  useWalkthroughManagerStore,
} from '../../stores/WalkthroughManagerStore';

/**
 * Orchestrates the deterministic transition for tooltip steps.
 * Handles element finding, retrying, scrolling, and committing the display state.
 *
 * When activeIndex changes and differs from displayIndex, this hook initiates
 * the transition state machine, allowing the old step UI to remain visible
 * while the new step's target element is being found and scrolled into view.
 */
export function useTooltipStepTransition(step: TooltipWalkthroughStep) {
  const activeIndex = useWalkthroughManagerStore().hooks.useActiveStepIndex();
  const displayIndex = useWalkthroughManagerStore().hooks.useDisplayStepIndex();
  const transitionState = useWalkthroughManagerStore().hooks.useTooltipTransitionState();
  const { status: transitionStatus, targetStepIndex } = transitionState;
  const { getWalkthroughManagerStore } = useGetWalkthroughManagerStore();
  const containerEl = useContainerElementContext();

  // When activeIndex changes and differs from displayIndex, start transition
  useEffect(() => {
    const store = getWalkthroughManagerStore();
    const driverActions = store.driverActions;
    const maxRetryCount = store.driverState.tooltipTransitionState?.maxRetries ?? 3;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let removeScrollListener: (() => void) | null = null;
    let transitionedToFinding = false;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      removeScrollListener?.();
    };

    const setIdle = () => {
      driverActions.setTooltipTransitionState({
        status: 'idle',
        retryCount: 0,
        maxRetries: maxRetryCount,
        targetStepIndex: activeIndex ?? targetStepIndex,
      });
    };

    const moveToFinding = () => {
      if (transitionedToFinding) return;
      const currentStore = getWalkthroughManagerStore();
      const { driverState } = currentStore;
      if (driverState.activeIndex !== activeIndex) return;

      const currentTransition = driverState.tooltipTransitionState;
      if (
        currentTransition.status === 'finding_element' ||
        currentTransition.status === 'scrolling' ||
        currentTransition.status === 'retrying' ||
        currentTransition.status === 'ready'
      ) {
        return;
      }

      transitionedToFinding = true;
      cleanup();
      driverActions.setTooltipTransitionState({
        status: 'finding_element',
        retryCount: 0,
        maxRetries: currentTransition.maxRetries ?? maxRetryCount,
        targetStepIndex: activeIndex ?? -1,
      });
    };

    // If no active step or already in sync, ensure we are idle (but only if not already idle)
    if (activeIndex === undefined || activeIndex === displayIndex) {
      if (transitionStatus && transitionStatus !== 'idle') {
        setIdle();
      }
      return cleanup;
    }

    // Preparing gate for showAfter conditions on the incoming step
    const { showAfter, showAfterDelay = 0, showAfterScroll = 0 } = step;

    if (showAfter === 'delay' && showAfterDelay > 0) {
      if (transitionStatus !== 'preparing' || targetStepIndex !== activeIndex) {
        driverActions.setTooltipTransitionState({
          status: 'preparing',
          retryCount: 0,
          maxRetries: maxRetryCount,
          targetStepIndex: activeIndex ?? -1,
        });
      }

      timeoutId = setTimeout(() => {
        moveToFinding();
      }, showAfterDelay);

      return cleanup;
    }

    if (showAfter === 'scroll' && showAfterScroll > 0) {
      if (transitionStatus !== 'preparing' || targetStepIndex !== activeIndex) {
        driverActions.setTooltipTransitionState({
          status: 'preparing',
          retryCount: 0,
          maxRetries: maxRetryCount,
          targetStepIndex: activeIndex ?? -1,
        });
      }

      const scrollContainer =
        (containerEl?.closest(ARENA_SELECTOR) as HTMLElement | null) || containerEl;
      const scrollTarget: HTMLElement | Window = scrollContainer || window;

      const getScrollMetrics = () => {
        if (scrollContainer) {
          const scrollTop = scrollContainer.scrollTop;
          const scrollHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
          return { scrollTop, scrollHeight };
        }
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight =
          document.documentElement.scrollHeight - document.documentElement.clientHeight;
        return { scrollTop, scrollHeight };
      };

      const handleScroll = () => {
        const { scrollTop, scrollHeight } = getScrollMetrics();
        const currentScrollPercentage = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
        if (currentScrollPercentage >= showAfterScroll) {
          moveToFinding();
          removeScrollListener?.();
        }
      };

      handleScroll();

      if (!transitionedToFinding) {
        scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
        removeScrollListener = () => scrollTarget.removeEventListener('scroll', handleScroll);
      }

      return cleanup;
    }

    // Default: no gating, move directly to element finding
    moveToFinding();

    return cleanup;
  }, [
    activeIndex,
    displayIndex,
    step,
    transitionStatus,
    targetStepIndex,
    getWalkthroughManagerStore,
    containerEl,
  ]);

  return {
    isTransitioning: transitionStatus !== 'idle',
  };
}
